import React, { useRef, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { copyToClipboard } from '../../../features/event-form-feature/payment/paymentFormUtils';

interface PixCopyCodeFieldProps {
  code: string;
  label?: string;
}

const PixCopyCodeField: React.FC<PixCopyCodeFieldProps> = ({ code, label = 'Código Pix' }) => {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const handleCopy = async () => {
    if (!code) return;
    const ok = await copyToClipboard(code);
    if (ok) {
      setCopied(true);
      setCopyFailed(false);
      window.setTimeout(() => setCopied(false), 2000);
      return;
    }
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.select();
    }
    setCopyFailed(true);
  };

  return (
    <Box sx={{ width: '100%', mt: 2, textAlign: 'left' }}>
      <Box
        sx={{
          display: 'flex',
          gap: 1.25,
          alignItems: 'stretch',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <TextField
          label={label}
          value={code}
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          inputRef={inputRef}
          inputProps={{
            inputMode: 'text',
            autoComplete: 'off',
            autoCorrect: 'off',
            autoCapitalize: 'none',
            spellCheck: false,
          }}
          InputProps={{
            readOnly: true,
            sx: {
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              wordBreak: 'break-all',
            },
          }}
          onFocus={(e) => e.target.select()}
          sx={{ flex: '1 1 auto', minWidth: 0 }}
        />
        <Button
          variant="contained"
          color={copied ? 'success' : 'primary'}
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={() => void handleCopy()}
          aria-label="Copiar código Pix"
          sx={{
            flexShrink: 0,
            minWidth: { xs: '100%', sm: 148 },
            minHeight: 48,
            px: 2.5,
            whiteSpace: 'nowrap',
          }}
        >
          {copied ? 'Copiado!' : 'Copiar código'}
        </Button>
      </Box>
      {copyFailed ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Se não copiar, toque no código, segure e escolha Copiar.
        </Typography>
      ) : null}
    </Box>
  );
};

export default PixCopyCodeField;
